import { useState, useEffect, useContext, useRef } from "react";
import {
  DataContext,
  LikeButtonFlagContext,
  ModalContext,
  RenderContext,
} from "./CreateContext";
import { useAtom } from "jotai";
import { currentMenuNumAtom } from "./atoms/currentMenuState";
import { Box, Flex } from "@chakra-ui/react";
import { Layout1 } from "./Layout1";
import Notfound from "./404";
import styles from ".././css/nonScrollbar.module.scss";
import "../css/MenuLoadingSpinner.scss";
import { WidthCalculation } from "./SizeCaluculation";
import { useFetchTopics } from "./hooks/useFetchTopics";
import { useFetchSettings } from "./hooks/useFetchSettings";
import { Data } from "./types/TypeData";
import { UrgentDeliver } from "./UrgentDelivery";

/**
 * `SnapFlexGrid`コンポーネントは、指定されたカテゴリーIDに基づいてトピックデータを非同期に取得し、データが読み込まれるとそれに基づいてレイアウトを表示します。
 *
 * 主な機能:
 * - `categoryId` に基づき、対応するデータを取得
 * - ローディング中にスピナーを表示
 * - データが存在しない場合は404画面を表示
 * - full_screenがtrueのデータがある場合はUrgentDeliverを表示
 * - データがある場合は、スクロール可能な3つのカラムに分けて記事を表示
 *
 * また、右クリック禁止やドラッグ禁止などの操作も制限しています。
 *
 * - `useEffect`を使用してデータの取得処理や、Likeボタンの状態更新を行います。
 * - `useContext`を使用して、状態管理やモーダルの表示を制御します。
 *
 * @param categoryId - カテゴリーID（例: "headline", "facebook" など）
 */

type Props = {
  categoryId: string;
};

const SnapFlexGrid: React.FC<Props> = (props) => {
  const { categoryId } = props;

  const { setModalData } = useContext(DataContext);
  const { setRerender, rerender } = useContext(RenderContext);
  const { LikeFlag, setLikeFlag } = useContext(LikeButtonFlagContext);
  const { isModalOpen } = useContext(ModalContext);
  const [currentMenuNum] = useAtom(currentMenuNumAtom);

  const isFirstRender = useRef(true);
  const [loading, setLoading] = useState(true);
  const { data, fetchTopics } = useFetchTopics();
  const { fetchSettings } = useFetchSettings();
  const [selectData, setSelectData] = useState<{ fixed: Data[]; move: Data[] }>(
    data
  );
  const [fullScreenData, setFullScreenData] = useState<Data[]>([]);

  useEffect(() => {
    setRerender(true);
  }, [rerender]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true); // ローディングを開始

      // 非同期関数を順番に呼び出す
      // categoryId10を追加しているのは、full_screenがtrueのデータを取得するため
      // 緊急配信が登録されていない場合は、画面に緊急配信が表示されることはない
      await fetchSettings();
      switch (categoryId) {
        case "university_news":
          await fetchTopics([5, 10]);
          break;
        case "twitter":
          await fetchTopics([2, 10]);
          break;
        case "youtube":
          await fetchTopics([4, 10]);
          break;
        case "instagram":
          await fetchTopics([3, 10]);
          break;
      }
      setLoading(false); // データの取得が完了したらローディングを終了
    };

    fetchData(); // 非同期処理を実行
  }, [rerender, currentMenuNum]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setModalData(data);
  }, [data]);

  useEffect(() => {
    if (data) {
      setSelectData(data);
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      setModalData(data);
    }
  }, [data]);

  // full_screenがtrueのデータを取得
  useEffect(() => {
    const filteredData = data.move.filter((item) => item.full_screen === true);
    if (data) {
      setFullScreenData(filteredData);
    }
  }, [data]);

  useEffect(() => {
    const fetchData = async () => {
      switch (categoryId) {
        case "university_news":
          await fetchTopics([5, 10]);
          break;
        case "twitter":
          await fetchTopics([2, 10]);
          break;
        case "youtube":
          await fetchTopics([4, 10]);
          break;
        case "instagram":
          await fetchTopics([3, 10]);
          break;
      }
    };

    if (LikeFlag && !isModalOpen) {
      console.log("いいね押した後に、詳細画面を閉じた");
      fetchData(); // 非同期処理を実行
      setLikeFlag(false);
    } else if (LikeFlag && isModalOpen) {
      console.log("いいね押した後に、詳細画面を開いた");
      fetchData(); // 非同期処理を実行
      setLikeFlag(true);
    }
  }, [isModalOpen]);

  // ローディング中はスピナーを表示
  if (loading) {
    return (
      <div
        style={{
          position: "absolute",
          width: window.innerHeight > window.innerWidth ? "100%" : "80%",
          height: "100vh",
        }}
      >
        <div
          className="sk-cube-grid"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
          }}
        >
          <div className="sk-cube sk-cube1"></div>
          <div className="sk-cube sk-cube2"></div>
          <div className="sk-cube sk-cube3"></div>
          <div className="sk-cube sk-cube4"></div>
          <div className="sk-cube sk-cube5"></div>
          <div className="sk-cube sk-cube6"></div>
          <div className="sk-cube sk-cube7"></div>
          <div className="sk-cube sk-cube8"></div>
          <div className="sk-cube sk-cube9"></div>
        </div>
      </div>
    );
  }
  // データが無い場合は404を表示
  else if (data.move.length === 0) {
    return (
      <div
        style={{
          position: "absolute",
          width: window.innerHeight > window.innerWidth ? "100%" : "80%",
          height: "100vh",
        }}
      >
        <Notfound />
      </div>
    );
  }

  // full_screenがtrueのdデータがある場合はUrgentDeliverを表示
  else if (fullScreenData.length === 1) {
    return (
      <div
        style={{
          position: "absolute",
          width: window.innerHeight > window.innerWidth ? "100%" : "80%",
          height: "100vh",
        }}
      >
        <UrgentDeliver data={fullScreenData[0]} />
      </div>
    );
  }

  // データがある場合は記事をsnap表示
  else if (data.move.length >= 1) {
    return (
      <Flex
        position="absolute"
        w={window.innerHeight > window.innerWidth ? "100%" : "80%"}
        overflow="hidden"
        pr="20px"
        pt="0px"
        pl={`${WidthCalculation(0, 20)}px`}
        zIndex="1"
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            flex="1"
            overflowY="scroll"
            overflowX="hidden"
            textAlign="center"
            className={styles.nonScrollbar}
          >
            <Layout1 key={i} i={i} moveData={selectData.move} />
          </Box>
        ))}
      </Flex>
    );
  } else {
    return null;
  }
};

export default SnapFlexGrid;
